import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  ListChecks, Clock, AlertTriangle, Users, Plus, CheckCircle2,
  Circle, Timer, Ban, Filter, Activity, Settings2,
  BarChart3, MessageSquare, UserCheck, X, Save, RefreshCw,
  FileText, Copy, TrendingUp, TrendingDown, Minus, ChevronDown,
  ChevronRight, Pencil, History, Shield, Zap, Target, Mail, Briefcase,
} from "lucide-react";
import { API } from "@/lib/constants";
import BoardViews, { fmt, announceTaskChange } from "@/components/scrum/BoardViews";

// Module-scope constant (stable across renders) — keeps useMemo deps clean.
const DONE_STATUSES = ["Completed", "Done", "Cancelled", "Declined", "Not Proceeding", "Won Back"];
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaskCompletionDialog } from "@/components/TaskCompletionDialog";
import { SmartDatePicker } from "@/components/SmartDatePicker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const TASK_STATUSES = ["Proposed", "Not Started", "In Progress", "Awaiting Client Response", "Completed", "Cancelled"];
const TASK_TYPES = ["general", "reactivation", "follow_up", "delivery"];
const PRIORITIES = ["low", "medium", "high", "critical"];
const LINK_TYPES = [
  { value: "client", label: "Client" },
  { value: "internal", label: "Internal" },
];

const STATUS_COLORS = {
  Proposed: "bg-slate-100 text-slate-700 border-slate-200",
  Accepted: "bg-blue-100 text-blue-700 border-blue-200",
  "In Progress": "bg-amber-100 text-amber-800 border-amber-200",
  Blocked: "bg-red-100 text-red-700 border-red-200",
  Completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Cancelled: "bg-slate-200 text-slate-500 border-slate-300",
  "Awaiting Client Response": "bg-purple-100 text-purple-700 border-purple-300",
  "On Hold": "bg-orange-100 text-orange-700 border-orange-200",
  Declined: "bg-slate-200 text-slate-500 border-slate-300",
  "Not Started": "bg-slate-100 text-slate-700 border-slate-200",
};

const PRIORITY_COLORS = {
  low: "bg-slate-50 text-slate-600 border-slate-200",
  medium: "bg-blue-50 text-blue-700 border-blue-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  critical: "bg-red-50 text-red-700 border-red-200",
};

const AGE_COLORS = { green: "text-emerald-600", amber: "text-amber-600", red: "text-red-600", done: "text-slate-400" };

const STATUS_ICONS = {
  Proposed: Circle, Accepted: UserCheck, "In Progress": Timer,
  Blocked: Ban, Completed: CheckCircle2, Cancelled: X, "Not Started": Circle, "On Hold": Clock,
  "Awaiting Client Response": Clock,
};

function fmtFull(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return d; }
}



// ─── Client Search (with debounce) ──
function ClientSearch({ value, clientName, onChange, onClear }) {
  const [query, setQuery] = useState(clientName || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = { current: null };

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    try {
      const res = await fetch(`${API}/scrum/v2/clients/search?q=${encodeURIComponent(q)}`, { credentials: "include" });
      if (res.ok) setResults(await res.json());
    } catch { /* ignore */ }
  }, []);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(v), 250);
    if (!v) { onChange("", ""); setResults([]); }
  };

  return (
    <div className="relative">
      <div className="flex gap-1">
        <Input
          value={query}
          onChange={handleInput}
          onFocus={() => { if (query.length >= 2) setOpen(true); }}
          placeholder="Search clients..."
          className="text-xs h-8"
          data-testid="client-search-input"
        />
        {value && (
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={() => { setQuery(""); onChange("", ""); onClear?.(); setResults([]); }}>
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto" data-testid="client-search-results">
          {results.map(c => (
            <div
              key={c.client_id}
              className="px-3 py-1.5 text-xs hover:bg-blue-50 cursor-pointer flex justify-between"
              onClick={() => {
                setQuery(c.company_name);
                onChange(c.client_id, c.company_name);
                setOpen(false);
              }}
            >
              <span className="font-medium text-slate-800">{c.company_name}</span>
              {c.sector && <span className="text-slate-400">{c.sector}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Summary Cards ──────────────────
function SummaryCards({ summary, activeFilter, onCardClick }) {
  const cards = [
    { label: "Active", key: "active", value: summary.total_active, icon: ListChecks, color: "text-blue-600", bg: "bg-blue-50", ring: "ring-blue-400" },
    { label: "Overdue", key: "overdue", value: summary.overdue, icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50", ring: "ring-red-400" },
    { label: "Needs decision", key: "needs_decision", value: summary.needs_decision, icon: Clock, color: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-400" },
    { label: "In Progress", key: "in_progress", value: summary.in_progress, icon: Timer, color: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-400" },
    { label: "Done (7d)", key: "done_7d", value: summary.completed_this_week, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-400" },
    { label: "Proposed", key: "proposed", value: summary.proposed, icon: Circle, color: "text-slate-600", bg: "bg-slate-50", ring: "ring-slate-400" },
  ];
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="scrum-summary-cards">
      {cards.map(c => {
        const isActive = activeFilter === c.key;
        return (
          <Card
            key={c.label}
            className={`border cursor-pointer transition-all duration-200 ${isActive ? `ring-2 ${c.ring} shadow-md scale-[1.02]` : "hover:shadow-md hover:scale-[1.01]"}`}
            onClick={() => onCardClick(c.key)}
            data-testid={`summary-card-${c.key}`}
          >
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <div className={`p-1.5 rounded-lg ${c.bg}`}><c.icon className={`w-3.5 h-3.5 ${c.color}`} /></div>
                <span className={`text-xl font-bold font-['Manrope'] ${c.value > 0 && c.label === "Overdue" ? "text-red-600" : "text-slate-900"}`}>{c.value || 0}</span>
              </div>
              <p className={`text-[10px] uppercase tracking-wider ${isActive ? c.color + " font-semibold" : "text-slate-500"}`}>{c.label}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Task Row ──────────────
function TaskRow({ task, users, onUpdate, onAddNote, onEdit }) {
  const [showNotes, setShowNotes] = useState(false);
  const [noteText, setNoteText] = useState("");
  const SIcon = STATUS_ICONS[task.status] || Circle;

  return (
    <>
      <TableRow
        className={`group ${task.is_stale ? "bg-amber-50/40" : ""} ${task.is_overdue ? "bg-red-50/30" : ""} hover:bg-slate-50 transition-colors cursor-pointer`}
        data-testid={`task-row-${task.task_id}`}
      >
        <TableCell className="w-8"><SIcon className={`w-4 h-4 ${task.status === "Completed" ? "text-emerald-500" : task.status === "Blocked" ? "text-red-500" : "text-slate-400"}`} /></TableCell>
        <TableCell onClick={() => onEdit(task)}>
          <p className="font-medium text-slate-800 text-sm">{task.title}</p>
          {task.client_name ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full mt-0.5">
              <Target className="w-2.5 h-2.5" />{task.client_name}
            </span>
          ) : (
            task.link_type !== "client" && <span className="text-[10px] text-slate-400 mt-0.5">Internal</span>
          )}
          {task.status === "Awaiting Client Response" && (
            <span className="inline-flex items-center gap-1 text-[10px] text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-full mt-0.5 ml-1" data-testid={`awaiting-client-badge-${task.task_id}`}>
              <Clock className="w-2.5 h-2.5" />With client — not your outstanding work
            </span>
          )}
        </TableCell>
        <TableCell><Badge variant="outline" className={`text-[10px] ${PRIORITY_COLORS[task.priority] || ""}`}>{task.priority}</Badge></TableCell>
        <TableCell>
          <Select value={task.status} onValueChange={v => onUpdate(task.task_id, { status: v })}>
            <SelectTrigger className="h-7 w-[120px] border-0 bg-transparent text-xs p-0">
              <Badge className={`${STATUS_COLORS[task.status] || "bg-slate-100"} text-[10px] border`}>{task.status}</Badge>
            </SelectTrigger>
            <SelectContent>{TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <Select value={task.assigned_to_id || "unassigned"} onValueChange={v => {
            if (v === "unassigned") onUpdate(task.task_id, { assigned_to_id: "", assigned_to_name: "" });
            else { const u = users.find(u => u.user_id === v); onUpdate(task.task_id, { assigned_to_id: v, assigned_to_name: u?.name || "" }); }
          }}>
            <SelectTrigger className="h-7 w-[110px] border-0 bg-transparent text-xs p-0">
              <span className={task.assigned_to_name ? "text-slate-700" : "text-slate-400"}>{task.assigned_to_name || task.owner_name || "Unassigned"}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {users.filter(u => u.role !== "viewer" && u.is_active !== false).map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <span className={`text-xs ${AGE_COLORS[task.age_status] || "text-slate-600"}`}>
            {task.days_since_assigned > 0 ? `${task.days_since_assigned}d` : "-"}
          </span>
        </TableCell>
        <TableCell>
          <span className={`text-xs ${task.is_overdue ? "text-red-600 font-semibold" : "text-slate-600"}`}>
            {fmt(task.due_date)}
            {task.is_overdue && <AlertTriangle className="inline w-3 h-3 ml-0.5 text-red-500" />}
          </span>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            {task.is_stale && <Badge className="bg-amber-100 text-amber-700 text-[10px] border border-amber-200">Stale</Badge>}
            {task.status === "Awaiting Client Response" && (
              <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 border-purple-300 text-purple-700 hover:bg-purple-50" data-testid={`client-replied-${task.task_id}`} onClick={() => onUpdate(task.task_id, { status: "In Progress" })}>Client replied</Button>
            )}
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100" onClick={() => setShowNotes(!showNotes)}><MessageSquare className="w-3 h-3 text-slate-400" /></Button>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100" onClick={() => onEdit(task)}><Pencil className="w-3 h-3 text-slate-400" /></Button>
          </div>
        </TableCell>
      </TableRow>
      {showNotes && (
        <TableRow><TableCell colSpan={8} className="bg-slate-50/80 p-3">
          <div className="space-y-2">
            {task.notes?.length > 0 ? task.notes.map((n, i) => (
              <div key={n.note_id || i} className="text-xs border-l-2 border-blue-200 pl-2">
                <span className="font-medium text-slate-700">{n.author_name}</span>
                <span className="text-slate-400 ml-2">{fmt(n.created_at)}</span>
                <p className="text-slate-600 mt-0.5">{n.text}</p>
              </div>
            )) : <p className="text-xs text-slate-400">No notes yet</p>}
            <div className="flex gap-2 mt-2">
              <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." className="text-xs h-7"
                onKeyDown={e => { if (e.key === "Enter" && noteText.trim()) { onAddNote(task.task_id, noteText.trim()); setNoteText(""); } }} />
              <Button size="sm" className="h-7 text-xs" disabled={!noteText.trim()} onClick={() => { onAddNote(task.task_id, noteText.trim()); setNoteText(""); }}>Add</Button>
            </div>
          </div>
        </TableCell></TableRow>
      )}
    </>
  );
}

// ─── Audit Timeline ─────────
function AuditTimeline({ audit }) {
  if (!audit?.length) return <p className="text-xs text-slate-400 py-4 text-center">No changes recorded yet</p>;
  return (
    <div className="space-y-3 max-h-[350px] overflow-y-auto pr-2" data-testid="audit-timeline">
      {audit.map(a => (
        <div key={a.audit_id} className="relative pl-5 pb-3 border-l-2 border-slate-200 last:border-0">
          <div className="absolute left-[-5px] top-1 w-2 h-2 rounded-full bg-blue-400" />
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-700">{a.user_name}</span>
            <span className="text-[10px] text-slate-400">{fmtFull(a.timestamp)}</span>
          </div>
          <p className="text-xs text-slate-500 capitalize">{a.action?.replace("_", " ")}</p>
          {a.changes && Object.entries(a.changes).map(([field, change]) => (
            <div key={field} className="mt-1 text-xs">
              <span className="text-slate-500">{field}: </span>
              {change.from !== undefined && <span className="line-through text-red-400 mr-1">{String(change.from || "-")}</span>}
              <span className="text-emerald-600 font-medium">{String(change.to || "-")}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Accountability Card ────
function AccountabilityCard({ score }) {
  const s = score.total_score;
  const barColor = s >= 70 ? "bg-emerald-500" : s >= 40 ? "bg-amber-500" : "bg-red-500";
  const TrendIcon = score.trend === "up" ? TrendingUp : score.trend === "down" ? TrendingDown : Minus;
  const trendColor = score.trend === "up" ? "text-emerald-500" : score.trend === "down" ? "text-red-500" : "text-slate-400";

  return (
    <Card className="border hover:shadow-md transition-shadow" data-testid={`accountability-${score.user_id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-semibold text-slate-800 text-sm">{score.name}</p>
            <p className="text-[10px] text-slate-500">{score.email}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`text-2xl font-bold font-['Manrope'] ${s >= 70 ? "text-emerald-600" : s >= 40 ? "text-amber-600" : "text-red-600"}`}>{s}</span>
            <TrendIcon className={`w-4 h-4 ${trendColor}`} />
          </div>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-1.5 mb-3">
          <div className={`${barColor} h-1.5 rounded-full transition-all duration-500`} style={{ width: `${Math.min(s, 100)}%` }} />
        </div>
        <div className="space-y-1.5">
          {Object.entries(score.breakdown).map(([key, b]) => (
            <div key={key} className="flex items-center justify-between text-xs">
              <span className="text-slate-500 capitalize">{key.replace(/_/g, " ")}</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-400">{b.detail}</span>
                <span className="font-medium text-slate-700 w-8 text-right">{b.score}/{b.max}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Velocity Chart ─────────
function VelocityChart({ data }) {
  if (!data?.weeks?.length) return null;
  const maxVal = Math.max(...data.weeks.flatMap(w => [w.created, w.completed]), 1);
  const momentumColors = { gaining: "text-emerald-600", losing: "text-red-600", steady: "text-slate-600" };

  return (
    <Card className="border" data-testid="velocity-chart">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Week-over-Week Velocity</CardTitle>
          <div className="flex items-center gap-3 text-xs">
            <span className={`font-medium ${momentumColors[data.momentum]}`}>
              Momentum: {data.momentum}
            </span>
            <span className="text-slate-500">Backlog: {data.current_backlog}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-1 h-32">
          {data.weeks.map(w => (
            <div key={w.week} className="flex-1 flex flex-col items-center gap-0.5">
              <div className="w-full flex justify-center gap-0.5" style={{ height: "100px" }}>
                <div className="w-3 bg-blue-200 rounded-t" style={{ height: `${(w.created / maxVal) * 100}px`, marginTop: "auto" }} title={`Created: ${w.created}`} />
                <div className="w-3 bg-emerald-400 rounded-t" style={{ height: `${(w.completed / maxVal) * 100}px`, marginTop: "auto" }} title={`Completed: ${w.completed}`} />
              </div>
              <span className="text-[9px] text-slate-500">{w.week}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-4 mt-2 text-[10px] text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-blue-200" />Created ({data.avg_created_per_week}/wk)</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-400" />Completed ({data.avg_completed_per_week}/wk)</span>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Agenda View ────────────
function AgendaView({ agenda, onCopy }) {
  if (!agenda?.sections) return null;
  const sections = Object.entries(agenda.sections);

  return (
    <div className="space-y-4" data-testid="meeting-agenda">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Scrum Meeting Agenda — {agenda.date}</h3>
          {agenda.scrum_master && <p className="text-xs text-slate-500">Led by {agenda.scrum_master}</p>}
        </div>
        <Button variant="outline" size="sm" onClick={onCopy} data-testid="copy-agenda-btn"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
      </div>

      {sections.map(([key, section]) => (
        <Card key={key} className={`border ${section.count === 0 ? "opacity-50" : ""}`}>
          <CardHeader className="py-2 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-semibold uppercase tracking-wider text-slate-600">{section.title}</CardTitle>
              <Badge variant="outline" className="text-[10px]">{section.count}</Badge>
            </div>
          </CardHeader>
          {section.count > 0 && (
            <CardContent className="px-4 pb-3 pt-0">
              <div className="space-y-1.5">
                {section.tasks.slice(0, 10).map(t => (
                  <div key={t.task_id} className="flex items-center justify-between text-xs py-1 border-b border-slate-50 last:border-0">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-slate-800">{t.title}</span>
                      {t.client_name && <span className="text-slate-400 ml-1">({t.client_name})</span>}
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <span className="text-slate-500">{t.assigned_to_name || t.owner_name || "Unassigned"}</span>
                      {t.due_date && <span className="text-slate-400">{fmt(t.due_date)}</span>}
                    </div>
                  </div>
                ))}
                {section.count > 10 && <p className="text-[10px] text-slate-400">+ {section.count - 10} more</p>}
              </div>
            </CardContent>
          )}
        </Card>
      ))}

      {agenda.person_summary?.length > 0 && (
        <Card className="border">
          <CardHeader className="py-2 px-4">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-slate-600">Per-Person Summary</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {agenda.person_summary.map(p => (
                <div key={p.user_id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-xs">
                  <div>
                    <span className="font-medium text-slate-800">{p.name}</span>
                    {p.days_inactive > 2 && <span className="text-red-500 ml-1">(inactive {p.days_inactive}d)</span>}
                  </div>
                  <div className="flex gap-3 text-[10px]">
                    <span className="text-slate-500">{p.active_tasks} active</span>
                    {p.overdue > 0 && <span className="text-red-600 font-medium">{p.overdue} overdue</span>}
                    {p.stale > 0 && <span className="text-amber-600">{p.stale} stale</span>}
                    <span className="text-emerald-600">{p.completed_this_week} done</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {agenda.client_breakdown?.length > 0 && (
        <Card className="border" data-testid="agenda-client-breakdown">
          <CardHeader className="py-2 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-semibold uppercase tracking-wider text-slate-600">By Client</CardTitle>
              <Badge variant="outline" className="text-[10px]">{agenda.client_breakdown.length} clients</Badge>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-3 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {agenda.client_breakdown.map(cb => (
                <div key={cb.client_id || cb.client_name} className="flex items-center justify-between p-2 rounded-lg bg-blue-50/50 text-xs">
                  <div className="flex items-center gap-1.5">
                    <Target className="w-3 h-3 text-blue-500" />
                    <span className="font-medium text-slate-800">{cb.client_name}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{cb.count} task{cb.count !== 1 ? "s" : ""}</Badge>
                </div>
              ))}
              {agenda.internal_tasks_count > 0 && (
                <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-xs">
                  <span className="font-medium text-slate-600">Internal</span>
                  <Badge variant="outline" className="text-[10px]">{agenda.internal_tasks_count} task{agenda.internal_tasks_count !== 1 ? "s" : ""}</Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Person View ────────────
function PersonView({ scores, tasks, activities }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="space-y-4" data-testid="person-view">
      {scores.map(s => {
        const personTasks = tasks.filter(t => (t.assigned_to_id === s.user_id || t.owner_name === s.name) && !["Completed", "Cancelled", "On Hold"].includes(t.status));
        const personActivities = activities.filter(a => a.user_id === s.user_id).slice(0, 8);
        const isOpen = expanded === s.user_id;

        return (
          <Card key={s.user_id} className="border hover:shadow-md transition-shadow">
            <CardContent className="p-0">
              <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => setExpanded(isOpen ? null : s.user_id)}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${s.total_score >= 70 ? "bg-emerald-100 text-emerald-700" : s.total_score >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                  {s.total_score}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 text-sm">{s.name}</p>
                  <p className="text-[10px] text-slate-500">{s.completed_count} completed | {s.overdue_count} overdue | {s.active_days}/{s.working_days} days active</p>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span>{personTasks.length} active tasks</span>
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </div>
              </div>
              {isOpen && (
                <div className="border-t px-4 pb-4 pt-3">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Active Tasks</h4>
                      {personTasks.length === 0 ? <p className="text-xs text-slate-400">No active tasks</p> : (
                        <div className="space-y-1.5">
                          {personTasks.slice(0, 8).map(t => (
                            <div key={t.task_id} className="flex items-center justify-between text-xs p-1.5 rounded bg-slate-50">
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-slate-800">{t.title}</span>
                                {t.client_name && <span className="text-slate-400 ml-1">({t.client_name})</span>}
                              </div>
                              <div className="flex items-center gap-2 ml-2">
                                <Badge className={`${STATUS_COLORS[t.status]} text-[9px] border`}>{t.status}</Badge>
                                <span className={`text-[10px] ${AGE_COLORS[t.age_status]}`}>{t.days_since_assigned > 0 ? `${t.days_since_assigned}d` : ""}</span>
                                {t.is_overdue && <AlertTriangle className="w-3 h-3 text-red-500" />}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Recent Activity</h4>
                      {personActivities.length === 0 ? <p className="text-xs text-slate-400">No recent activity</p> : (
                        <div className="space-y-1.5">
                          {personActivities.map(a => (
                            <div key={a.activity_id} className="flex items-start gap-2 text-xs">
                              <Activity className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span className="text-slate-600">{a.action_label}</span>
                                {a.detail && <span className="text-slate-400 ml-1 truncate">— {a.detail}</span>}
                              </div>
                              <span className="text-[10px] text-slate-400 whitespace-nowrap">{fmt(a.timestamp)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}



export default function ScrumManagement({ user, embedded = false }) {
  const [summary, setSummary] = useState({});
  const [boardViews, setBoardViews] = useState({ views: {}, counts: {} });
  const [outcomeNeeded, setOutcomeNeeded] = useState({ items: [], count: 0 });
  const [escalatedInbox, setEscalatedInbox] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [utilisation, setUtilisation] = useState([]);
  const [activities, setActivities] = useState([]);
  const [config, setConfig] = useState({});
  const [accountability, setAccountability] = useState([]);
  const [velocity, setVelocity] = useState(null);
  const [agenda, setAgenda] = useState(null);
  const [loading, setLoading] = useState(true);

  const [filterStatus, setFilterStatus] = useState("");
  const [filterAssignee, setFilterAssignee] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterLinkType, setFilterLinkType] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [completingTask, setCompletingTask] = useState(null);
  const [cardFilter, setCardFilter] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", description: "", task_type: "general", priority: "medium", assigned_to_id: "", assigned_to_name: "", link_type: "internal", client_id: "", client_name: "", due_date: "", category: "" });

  const [showConfig, setShowConfig] = useState(false);
  const [editConfig, setEditConfig] = useState({});

  const [editTask, setEditTask] = useState(null);
  const [editData, setEditData] = useState({});
  const [taskAudit, setTaskAudit] = useState([]);
  const [followUpTask, setFollowUpTask] = useState(null);
  const [followUpTitle, setFollowUpTitle] = useState("");
  const [followUpDueDate, setFollowUpDueDate] = useState("");
  const [completionMode, setCompletionMode] = useState("choose");
  const [waitChaseDate, setWaitChaseDate] = useState("");

  const [commitments, setCommitments] = useState(null);
  const [showCommit, setShowCommit] = useState(false);
  const [commitForm, setCommitForm] = useState({ title: "", source_section: "general", due_time: "17:00", client_name: "", founder_escalation: false });
  const [replyText, setReplyText] = useState("");
  const [replyResults, setReplyResults] = useState(null);

  const isAdmin = user?.role === "admin";

  const fetchAll = useCallback(async () => {
    try {
      const f = (url) => fetch(`${API}${url}`, { credentials: "include", cache: "no-store" });
      // When card filter is done_7d, we need completed tasks; when overdue/stale/active, we need all active
      const needCompleted = showCompleted || cardFilter === "done_7d";
      const [summaryR, tasksR, usersR, utilR, actR, accR, velR] = await Promise.all([
        f("/scrum/v2/summary"),
        f(`/scrum/v2/tasks?exclude_completed=${!needCompleted}${filterStatus ? `&status=${filterStatus}` : ""}${filterAssignee ? `&assigned_to_id=${filterAssignee}` : ""}${filterType ? `&task_type=${filterType}` : ""}${filterLinkType ? `&link_type=${filterLinkType}` : ""}`),
        f("/users"),
        f("/scrum/v2/utilisation"),
        f("/scrum/v2/activity?limit=50"),
        f("/scrum/v2/accountability"),
        f("/scrum/v2/velocity"),
      ]);
      if (summaryR.ok) setSummary(await summaryR.json());
      if (tasksR.ok) { const d = await tasksR.json(); setTasks(d.tasks || []); }
      if (usersR.ok) setUsers(await usersR.json());
      if (utilR.ok) { const d = await utilR.json(); setUtilisation(d.team || []); }
      if (actR.ok) { const d = await actR.json(); setActivities(d.activities || []); }
      if (accR.ok) { const d = await accR.json(); setAccountability(d.scores || []); }
      if (velR.ok) setVelocity(await velR.json());
      // Fetch commitments
      const commitR = await f("/scrum/v2/commitments/today");
      if (commitR.ok) setCommitments(await commitR.json());
      const bvR = await f("/scrum/v2/board-views");
      if (bvR.ok) setBoardViews(await bvR.json());
      const onR = await f("/scrum/v2/outcome-needed");
      if (onR.ok) setOutcomeNeeded(await onR.json());
      // I2: escalated inbox items (>= 3 business days) for scrum masters — surfaced in Needs decision.
      const staffRole = ["admin", "managing_partner", "scrum_master"].includes(user?.role);
      if (staffRole) {
        const escR = await f("/oauth/gmail/pending");
        if (escR.ok) { const d = await escR.json(); setEscalatedInbox(d.escalated || []); }
      }
    } catch (err) { console.error("Scrum fetch:", err); }
    finally { setLoading(false); }
  }, [showCompleted, cardFilter, filterStatus, filterAssignee, filterType, filterLinkType, user]);

  // Apply card filter on the client side
  const filteredTasks = useMemo(() => {
    if (!cardFilter) return tasks;
    switch (cardFilter) {
      case "active":
        return tasks.filter(t => !DONE_STATUSES.includes(t.status));
      case "overdue":
        return tasks.filter(t => t.is_overdue);
      case "stale":
        return tasks.filter(t => t.is_stale);
      case "in_progress":
        return tasks.filter(t => t.status === "In Progress");
      case "done_7d":
        return tasks.filter(t => t.status === "Completed" || t.status === "Done");
      case "proposed":
        return tasks.filter(t => t.status === "Proposed");
      default:
        return tasks;
    }
  }, [tasks, cardFilter]);

  const handleCardClick = (key) => {
    if (cardFilter === key) {
      // Toggle off — clear card filter and reset dropdowns
      setCardFilter("");
    } else {
      setCardFilter(key);
      // Clear dropdown filters when using card filter
      setFilterStatus("");
      setFilterAssignee("");
      setFilterType("");
      setFilterLinkType("");
    }
  };

  useEffect(() => {
    fetchAll();
    if (isAdmin) {
      fetch(`${API}/scrum/v2/config`, { credentials: "include" }).then(r => r.ok ? r.json() : {}).then(setConfig).catch(() => {});
    }
  }, [fetchAll, isAdmin]);

  useEffect(() => {
    // N3 Commit C — a task changed anywhere: drop it optimistically if completed, then refetch.
    const onDone = (e) => { const id = e.detail && e.detail.task_id; if (id) setTasks(prev => prev.filter(t => t.task_id !== id)); fetchAll(); };
    window.addEventListener("scrum:task-completed", onDone);
    return () => window.removeEventListener("scrum:task-completed", onDone);
  }, [fetchAll]);

  const handleUpdate = async (taskId, updates) => {
    // C1: no silent completion — terminal status must go through the completion step.
    if (updates.status && ["Completed", "Cancelled", "Done"].includes(updates.status)) {
      const t = tasks.find(x => x.task_id === taskId) || { task_id: taskId };
      setCompletingTask(t);
      return;
    }
    const res = await fetch(`${API}/scrum/v2/tasks/${taskId}`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(updates) });
    if (res.ok) {
      const updated = await res.json().catch(() => null);
      const terminal = ["Completed", "Done", "Cancelled"].includes(updates.status);
      const showingCompleted = showCompleted || cardFilter === "done_7d";
      if (updated && updated.task_id) {
        setTasks(prev => (terminal && !showingCompleted)
          ? prev.filter(t => t.task_id !== taskId)
          : prev.map(t => t.task_id === taskId ? { ...t, ...updated } : t));
      }
      toast.success("Task updated");
      announceTaskChange(taskId);
      // If task was marked Done/Completed, tell the other lists and prompt for follow-up
      if (updates.status === "Done" || updates.status === "Completed") {
        window.dispatchEvent(new CustomEvent("scrum:task-completed", { detail: { task_id: taskId } }));
        const completedTask = tasks.find(t => t.task_id === taskId);
        if (completedTask) {
          setCompletionMode("choose");
          const d = new Date(); d.setDate(d.getDate() + 10);
          setWaitChaseDate(d.toISOString().slice(0, 10));
          setFollowUpTask(completedTask);
          setFollowUpTitle(`Follow-up: ${completedTask.title}`);
          setFollowUpDueDate(completedTask.due_date || new Date().toISOString().slice(0, 10));
        }
      }
      fetchAll();
    }
    else { const err = await res.json().catch(() => null); toast.error(err?.detail || "Could not mark done. It is still on the board"); }
  };

  const handleAddNote = async (taskId, text) => {
    const res = await fetch(`${API}/scrum/v2/tasks/${taskId}/notes`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ text }) });
    if (res.ok) { toast.success("Note added"); announceTaskChange(taskId); fetchAll(); }
  };

  const handleCreate = async () => {
    if (!newTask.title.trim()) { toast.error("Title required"); return; }
    if (!newTask.due_date) { toast.error("Due date is required"); return; }
    if (!newTask.category) { toast.error("Category is required"); return; }
    const res = await fetch(`${API}/scrum/v2/tasks`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(newTask) });
    if (res.ok) { toast.success("Task created"); setShowCreate(false); setNewTask({ title: "", description: "", task_type: "general", priority: "medium", assigned_to_id: "", assigned_to_name: "", link_type: "internal", client_id: "", client_name: "", due_date: "", category: "", act_now: true, self_assigned: false }); announceTaskChange(); fetchAll(); }
    else if (res.status === 409) { const err = await res.json().catch(() => null); const m = err?.detail?.message || "Possible duplicate exists."; if (window.confirm(`${m}\n\nCreate anyway?`)) { const r2 = await fetch(`${API}/scrum/v2/tasks`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ ...newTask, confirm_duplicate: true }) }); if (r2.ok) { toast.success("Task created"); setShowCreate(false); announceTaskChange(); fetchAll(); } } }
    else { const err = await res.json().catch(() => null); toast.error(err?.detail || "Failed"); }
  };

  const handleSaveConfig = async () => {
    const res = await fetch(`${API}/scrum/v2/config`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(editConfig) });
    if (res.ok) { const d = await res.json(); setConfig(d); setShowConfig(false); toast.success("Config updated"); }
  };

  const openEditTask = async (task) => {
    setEditTask(task);
    setEditData({ title: task.title, description: task.description || "", status: task.status, priority: task.priority || "medium", assigned_to_id: task.assigned_to_id || "", assigned_to_name: task.assigned_to_name || "", due_date: task.due_date || "", task_type: task.task_type || "general", link_type: task.link_type || (task.client_id || task.client_name ? "client" : "internal"), client_id: task.client_id || "", client_name: task.client_name || "", category: task.category || "" });
    const res = await fetch(`${API}/scrum/v2/tasks/${task.task_id}/audit`, { credentials: "include" });
    if (res.ok) { const d = await res.json(); setTaskAudit(d.audit || []); }
  };

  const handleSaveEditTask = async () => {
    if (!editTask) return;
    const res = await fetch(`${API}/scrum/v2/tasks/${editTask.task_id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(editData) });
    if (res.ok) { const updated = await res.json().catch(() => null); if (updated && updated.task_id) setTasks(prev => prev.map(t => t.task_id === editTask.task_id ? { ...t, ...updated } : t)); toast.success("Task updated"); announceTaskChange(editTask.task_id); setEditTask(null); fetchAll(); }
    else { const err = await res.json().catch(() => null); toast.error(err?.detail || "Failed"); }
  };

  const generateAgenda = async () => {
    const res = await fetch(`${API}/scrum/v2/agenda`, { credentials: "include" });
    if (res.ok) setAgenda(await res.json());
  };

  const copyAgenda = () => {
    if (!agenda) return;
    let text = `SCRUM MEETING AGENDA — ${agenda.date}\nLed by: ${agenda.scrum_master}\n\n`;
    for (const [, s] of Object.entries(agenda.sections)) {
      text += `${s.title} (${s.count})\n`;
      s.tasks.slice(0, 10).forEach(t => { text += `  - ${t.title} | ${t.assigned_to_name || t.owner_name || "Unassigned"} | Due: ${fmt(t.due_date)}\n`; });
      text += "\n";
    }
    text += "PER-PERSON SUMMARY\n";
    agenda.person_summary?.forEach(p => { text += `  ${p.name}: ${p.active_tasks} active, ${p.overdue} overdue, ${p.completed_this_week} done${p.days_inactive > 2 ? ` (inactive ${p.days_inactive}d)` : ""}\n`; });
    navigator.clipboard.writeText(text).then(() => toast.success("Agenda copied to clipboard"));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-5" data-testid="scrum-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 font-['Manrope']">Scrum Management</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Accountability, velocity & team performance
            {config.primary_scrum_master_name && <span className="ml-2 text-blue-600">SM: {config.primary_scrum_master_name}</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-8" onClick={() => setShowCreate(true)} data-testid="create-task-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Task</Button>
          <Button variant="outline" size="sm" className="h-8" onClick={generateAgenda} data-testid="generate-agenda-btn"><FileText className="w-3.5 h-3.5 mr-1" /> Agenda</Button>
          <Button variant="outline" size="sm" className="h-8" onClick={fetchAll}><RefreshCw className="w-3.5 h-3.5" /></Button>
          {isAdmin && <Button variant="outline" size="sm" className="h-8" onClick={() => { setEditConfig({ ...config }); setShowConfig(true); }} data-testid="scrum-config-btn"><Settings2 className="w-3.5 h-3.5" /></Button>}
        </div>
      </div>

      <SummaryCards summary={summary} activeFilter={cardFilter} onCardClick={handleCardClick} />

      {/* Tabs */}
      <Tabs defaultValue="board">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="board" data-testid="tab-board"><ListChecks className="w-3.5 h-3.5 mr-1" />Board</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><ListChecks className="w-3.5 h-3.5 mr-1" />Tasks</TabsTrigger>
          <TabsTrigger value="people" data-testid="tab-people"><Users className="w-3.5 h-3.5 mr-1" />People</TabsTrigger>
          <TabsTrigger value="accountability" data-testid="tab-accountability"><Shield className="w-3.5 h-3.5 mr-1" />Scores</TabsTrigger>
          <TabsTrigger value="velocity" data-testid="tab-velocity"><Zap className="w-3.5 h-3.5 mr-1" />Velocity</TabsTrigger>
          <TabsTrigger value="agenda" data-testid="tab-agenda"><FileText className="w-3.5 h-3.5 mr-1" />Agenda</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity"><Activity className="w-3.5 h-3.5 mr-1" />Log</TabsTrigger>
        </TabsList>

        {/* Tasks */}
        <TabsContent value="board" className="mt-3">
          {outcomeNeeded.count > 0 && (
            <div className="border border-rose-200 rounded-lg mb-4" data-testid="outcome-needed-section">
              <div className="px-3 py-2 bg-rose-50 border-b flex items-center justify-between">
                <span className="font-semibold text-sm text-rose-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Outcome needed</span>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-rose-200 text-rose-900" data-testid="outcome-needed-count">{outcomeNeeded.count}</span>
              </div>
              <div className="divide-y">
                {outcomeNeeded.items.map((it) => (
                  <div key={it.task_id} className="px-3 py-2 flex items-center justify-between gap-2" data-testid={`outcome-needed-${it.task_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-slate-800 truncate">{it.title}</div>
                      <div className="text-xs text-slate-500">{it.assigned_to_name || "Unassigned"} · {it.task_type || "task"} · {it.reason}</div>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" data-testid={`outcome-add-${it.task_id}`}
                      onClick={() => setCompletingTask({ ...it, status: "Completed", _retro: true })}>Add outcome</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <BoardViews data={boardViews} user={user} onRefresh={fetchAll} apiBase={API} escalatedInbox={escalatedInbox} onOpenTask={openEditTask} />
        </TabsContent>

        <TabsContent value="tasks" className="mt-3">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            {cardFilter && (
              <button
                onClick={() => setCardFilter("")}
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-slate-800 text-white rounded-full hover:bg-slate-700 transition-colors"
                data-testid="clear-card-filter"
              >
                {cardFilter === "done_7d" ? "Done (7d)" : cardFilter.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())} &times;
              </button>
            )}
            <Select value={filterStatus || "all"} onValueChange={v => { setFilterStatus(v === "all" ? "" : v); setCardFilter(""); }}>
              <SelectTrigger className="w-[120px] h-7 text-xs" data-testid="filter-task-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Status</SelectItem>{TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterAssignee || "all"} onValueChange={v => { setFilterAssignee(v === "all" ? "" : v); setCardFilter(""); }}>
              <SelectTrigger className="w-[120px] h-7 text-xs"><SelectValue placeholder="Person" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All People</SelectItem>{users.filter(u => u.role !== "viewer" && u.is_active !== false).map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterType || "all"} onValueChange={v => { setFilterType(v === "all" ? "" : v); setCardFilter(""); }}>
              <SelectTrigger className="w-[120px] h-7 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Types</SelectItem>{TASK_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterLinkType || "all"} onValueChange={v => { setFilterLinkType(v === "all" ? "" : v); setCardFilter(""); }}>
              <SelectTrigger className="w-[120px] h-7 text-xs" data-testid="filter-link-type"><SelectValue placeholder="Linked To" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Tasks</SelectItem>{LINK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer ml-2">
              <input type="checkbox" checked={showCompleted} onChange={e => { setShowCompleted(e.target.checked); if (!e.target.checked) setCardFilter(""); }} className="rounded" /> Completed
            </label>
          </div>
          <Card className="border">
            <div className={`overflow-x-auto ${embedded ? "max-h-[520px] overflow-y-auto" : ""}`}>
              <Table>
                <TableHeader className={embedded ? "sticky top-0 z-10 bg-white" : ""}>
                  <TableRow className="text-[10px] uppercase">
                    <TableHead className="w-8" />
                    <TableHead>Task</TableHead>
                    <TableHead className="w-[70px]">Priority</TableHead>
                    <TableHead className="w-[130px]">Status</TableHead>
                    <TableHead className="w-[120px]">Assignee</TableHead>
                    <TableHead className="w-[60px]">Age</TableHead>
                    <TableHead className="w-[80px]">Due</TableHead>
                    <TableHead className="w-[80px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">{cardFilter ? `No ${cardFilter.replace("_", " ")} tasks found` : "No tasks found"}</TableCell></TableRow>
                  ) : filteredTasks.map(t => <TaskRow key={t.task_id} task={t} users={users} onUpdate={handleUpdate} onAddNote={handleAddNote} onEdit={openEditTask} />)}
                </TableBody>
              </Table>
            </div>
          </Card>
          <p className="text-[10px] text-slate-400 mt-1">{filteredTasks.length} task{filteredTasks.length !== 1 ? "s" : ""}{cardFilter ? ` (filtered)` : ""}</p>
        </TabsContent>

        {/* People */}
        <TabsContent value="people" className="mt-3">
          <PersonView scores={accountability} tasks={tasks} activities={activities} />
        </TabsContent>

        {/* Accountability */}
        <TabsContent value="accountability" className="mt-3">
          <p className="text-xs text-slate-500 mb-3">Accountability score based on: on-time completion (30pts), task ageing (25pts), activity frequency (25pts), overdue ratio (20pts)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accountability.map(s => <AccountabilityCard key={s.user_id} score={s} />)}
          </div>
        </TabsContent>

        {/* Velocity */}
        <TabsContent value="velocity" className="mt-3">
          <VelocityChart data={velocity} />
          <div className="mt-4 grid grid-cols-3 gap-4 text-center">
            <Card className="border p-4">
              <p className="text-xs text-slate-500 uppercase">Avg Created/Week</p>
              <p className="text-2xl font-bold text-blue-600 font-['Manrope']">{velocity?.avg_created_per_week || 0}</p>
            </Card>
            <Card className="border p-4">
              <p className="text-xs text-slate-500 uppercase">Avg Completed/Week</p>
              <p className="text-2xl font-bold text-emerald-600 font-['Manrope']">{velocity?.avg_completed_per_week || 0}</p>
            </Card>
            <Card className="border p-4">
              <p className="text-xs text-slate-500 uppercase">Current Backlog</p>
              <p className="text-2xl font-bold text-slate-800 font-['Manrope']">{velocity?.current_backlog || 0}</p>
            </Card>
          </div>
        </TabsContent>

        {/* Agenda */}
        <TabsContent value="agenda" className="mt-3">
          {agenda ? <AgendaView agenda={agenda} onCopy={copyAgenda} /> : (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500 mb-3">Generate a structured agenda for your scrum meeting</p>
              <Button onClick={generateAgenda} className="bg-blue-600 hover:bg-blue-700" data-testid="generate-agenda-cta"><FileText className="w-4 h-4 mr-2" /> Generate Meeting Agenda</Button>
            </div>
          )}
        </TabsContent>

        {/* Activity Log */}
        <TabsContent value="activity" className="mt-3">
          <Card className="border">
            <CardHeader className="pb-2"><CardTitle className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Recent Activity</CardTitle></CardHeader>
            <CardContent>
              {activities.length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No activity yet</p> : (
                <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                  {activities.map(a => (
                    <div key={a.activity_id} className="flex items-start gap-2 p-1.5 rounded hover:bg-slate-50 text-xs">
                      <Activity className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-slate-700">{a.user_name}</span>
                        <span className="text-slate-500 ml-1">{a.action_label}</span>
                        {a.detail && <span className="text-slate-400 ml-1">— {a.detail}</span>}
                      </div>
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">{fmt(a.timestamp)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Task Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Create Task</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Title *</Label><Input value={newTask.title} onChange={e => setNewTask(p => ({ ...p, title: e.target.value }))} placeholder="What needs to be done?" data-testid="new-task-title" /></div>
            <div className="rounded border border-slate-200 bg-slate-50 p-2" data-testid="act-now-question">
              <Label className="text-xs font-semibold">Can you act now, or are you waiting for someone or something?</Label>
              <div className="flex gap-2 mt-1">
                <button type="button" className={`text-xs px-2 py-1 rounded border ${newTask.act_now !== false ? "bg-teal-600 text-white border-teal-600" : "bg-white border-slate-300"}`} data-testid="act-now-yes" onClick={() => setNewTask(p => ({ ...p, act_now: true }))}>I can act now</button>
                <button type="button" className={`text-xs px-2 py-1 rounded border ${newTask.act_now === false ? "bg-amber-600 text-white border-amber-600" : "bg-white border-slate-300"}`} data-testid="act-now-wait" onClick={() => setNewTask(p => ({ ...p, act_now: false }))}>I'm waiting on someone/something</button>
              </div>
              {newTask.act_now === false && <p className="text-[11px] text-amber-700 mt-1">Waiting items should be recorded as a wait (counterparty, reason, review date) — use the wait flow, not a task.</p>}
            </div>
            <div><Label className="text-xs">Description</Label><Textarea value={newTask.description} onChange={e => setNewTask(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
            <div>
              <Label className="text-xs mb-1.5 block">Category <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-2 gap-2" data-testid="new-task-category">
                {[
                  {v:"COMMERCIAL",l:"Commercial",c:"bg-emerald-100 text-emerald-700 border-emerald-300"},
                  {v:"CLIENT_DELIVERY",l:"Client Delivery",c:"bg-blue-100 text-blue-700 border-blue-300"},
                  {v:"ADMIN_FINANCE",l:"Admin / Finance",c:"bg-slate-100 text-slate-700 border-slate-300"},
                  {v:"INTERNAL",l:"Internal",c:"bg-purple-100 text-purple-700 border-purple-300"},
                ].map(cat => (
                  <button
                    key={cat.v}
                    type="button"
                    data-testid={`new-task-cat-${cat.v}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded border-2 transition ${newTask.category === cat.v ? cat.c : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
                    onClick={() => setNewTask(p => ({ ...p, category: cat.v }))}
                  >{cat.l}</button>
                ))}
              </div>
            </div>
            {/* Link Type Toggle */}
            <div>
              <Label className="text-xs mb-1.5 block">Linked To</Label>
              <div className="flex gap-2" data-testid="link-type-toggle">
                {LINK_TYPES.map(lt => (
                  <Button
                    key={lt.value}
                    type="button"
                    variant={newTask.link_type === lt.value ? "default" : "outline"}
                    size="sm"
                    className={`h-7 text-xs ${newTask.link_type === lt.value ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                    onClick={() => {
                      setNewTask(p => ({ ...p, link_type: lt.value, ...(lt.value === "internal" ? { client_id: "", client_name: "" } : {}) }));
                    }}
                  >{lt.label}</Button>
                ))}
              </div>
            </div>
            {newTask.link_type === "client" && (
              <div>
                <Label className="text-xs">Client</Label>
                <ClientSearch
                  value={newTask.client_id}
                  clientName={newTask.client_name}
                  onChange={(id, name) => setNewTask(p => ({ ...p, client_id: id, client_name: name }))}
                  onClear={() => setNewTask(p => ({ ...p, client_id: "", client_name: "" }))}
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Priority</Label>
                <Select value={newTask.priority} onValueChange={v => setNewTask(p => ({ ...p, priority: v }))}><SelectTrigger data-testid="new-task-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Type</Label>
                <Select value={newTask.task_type} onValueChange={v => setNewTask(p => ({ ...p, task_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TASK_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace("_", " ")}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Assign To</Label>
                <Select value={newTask.assigned_to_id || "unassigned"} onValueChange={v => { if (v === "unassigned") setNewTask(p => ({ ...p, assigned_to_id: "", assigned_to_name: "" })); else { const u = users.find(u => u.user_id === v); setNewTask(p => ({ ...p, assigned_to_id: v, assigned_to_name: u?.name || "" })); } }}>
                  <SelectTrigger data-testid="new-task-assignee"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{users.filter(u => u.role !== "viewer" && u.is_active !== false).map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Due Date <span className="text-red-500">*</span></Label><Input type="date" required value={newTask.due_date} onChange={e => setNewTask(p => ({ ...p, due_date: e.target.value }))} data-testid="new-task-due-date" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button className="bg-blue-600 hover:bg-blue-700" disabled={!newTask.title.trim() || !newTask.category || !newTask.due_date} onClick={handleCreate} data-testid="submit-create-task">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Modal */}
      <Dialog open={!!editTask} onOpenChange={v => { if (!v) setEditTask(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4" /> Edit Task</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div><Label className="text-xs">Title</Label><Input value={editData.title || ""} onChange={e => setEditData(p => ({ ...p, title: e.target.value }))} data-testid="edit-task-title" /></div>
              <div><Label className="text-xs">Description</Label><Textarea value={editData.description || ""} onChange={e => setEditData(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
              <div>
                <Label className="text-xs mb-1.5 block">Category</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="edit-task-category">
                  {[
                    {v:"COMMERCIAL",l:"Commercial",c:"bg-emerald-100 text-emerald-700 border-emerald-300"},
                    {v:"CLIENT_DELIVERY",l:"Client Delivery",c:"bg-blue-100 text-blue-700 border-blue-300"},
                    {v:"ADMIN_FINANCE",l:"Admin / Finance",c:"bg-slate-100 text-slate-700 border-slate-300"},
                    {v:"INTERNAL",l:"Internal",c:"bg-purple-100 text-purple-700 border-purple-300"},
                  ].map(cat => (
                    <button
                      key={cat.v}
                      type="button"
                      data-testid={`edit-task-cat-${cat.v}`}
                      className={`text-xs font-semibold px-3 py-1.5 rounded border-2 transition ${editData.category === cat.v ? cat.c : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
                      onClick={() => setEditData(p => ({ ...p, category: cat.v }))}
                    >{cat.l}</button>
                  ))}
                </div>
              </div>
              {/* Link Type Toggle */}
              <div>
                <Label className="text-xs mb-1.5 block">Linked To</Label>
                <div className="flex gap-2" data-testid="edit-link-type-toggle">
                  {LINK_TYPES.map(lt => (
                    <Button
                      key={lt.value}
                      type="button"
                      variant={editData.link_type === lt.value ? "default" : "outline"}
                      size="sm"
                      className={`h-7 text-xs ${editData.link_type === lt.value ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                      onClick={() => {
                        setEditData(p => ({ ...p, link_type: lt.value, ...(lt.value === "internal" ? { client_id: "", client_name: "" } : {}) }));
                      }}
                    >{lt.label}</Button>
                  ))}
                </div>
              </div>
              {editData.link_type === "client" && (
                <div>
                  <Label className="text-xs">Client</Label>
                  <ClientSearch
                    value={editData.client_id}
                    clientName={editData.client_name}
                    onChange={(id, name) => setEditData(p => ({ ...p, client_id: id, client_name: name }))}
                    onClear={() => setEditData(p => ({ ...p, client_id: "", client_name: "" }))}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Status</Label>
                  <Select value={editData.status || ""} onValueChange={v => { if (["Completed", "Cancelled", "Done"].includes(v)) { setCompletingTask(editTask); } else { setEditData(p => ({ ...p, status: v })); } }} data-testid="edit-status-select"><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Priority</Label>
                  <Select value={editData.priority || "medium"} onValueChange={v => setEditData(p => ({ ...p, priority: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Assignee</Label>
                  <Select value={editData.assigned_to_id || "unassigned"} onValueChange={v => { if (v === "unassigned") setEditData(p => ({ ...p, assigned_to_id: "", assigned_to_name: "" })); else { const u = users.find(u => u.user_id === v); setEditData(p => ({ ...p, assigned_to_id: v, assigned_to_name: u?.name || "" })); } }}>
                    <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{users.filter(u => u.role !== "viewer" && u.is_active !== false).map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Due Date</Label><Input type="date" value={editData.due_date || ""} onChange={e => setEditData(p => ({ ...p, due_date: e.target.value }))} /></div>
              </div>
              <div><Label className="text-xs">Type</Label>
                <Select value={editData.task_type || "general"} onValueChange={v => setEditData(p => ({ ...p, task_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TASK_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace("_", " ")}</SelectItem>)}</SelectContent></Select></div>
              {editTask && (
                <div className="text-[10px] text-slate-400 space-y-0.5 pt-2 border-t">
                  <p>Created: {fmtFull(editTask.created_at)} by {editTask.created_by_name}</p>
                  {editTask.assigned_date && <p>Assigned: {fmtFull(editTask.assigned_date)}{editTask.days_since_assigned > 0 ? ` (${editTask.days_since_assigned} days ago)` : ""}</p>}
                  {editTask.completed_at && <p>Completed: {fmtFull(editTask.completed_at)}</p>}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <History className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Change History</span>
              </div>
              <AuditTimeline audit={taskAudit} />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditTask(null)}>Cancel</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSaveEditTask} data-testid="save-edit-task"><Save className="w-3.5 h-3.5 mr-1" />Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <TaskCompletionDialog task={completingTask} open={!!completingTask}
        onOpenChange={(v) => { if (!v) setCompletingTask(null); }}
        onDone={(res) => { const id = completingTask?.task_id; setCompletingTask(null); setEditTask(null); if (id) { setTasks(prev => prev.filter(t => t.task_id !== id)); window.dispatchEvent(new CustomEvent("scrum:task-completed", { detail: { task_id: id } })); } fetchAll(); }} />

      {/* Config Modal */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Scrum Configuration</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Primary Scrum Master</Label>
              <Select value={editConfig.primary_scrum_master_email || "none"} onValueChange={v => { if (v === "none") setEditConfig(p => ({ ...p, primary_scrum_master_email: "", primary_scrum_master_name: "" })); else { const u = users.find(u => u.email === v); setEditConfig(p => ({ ...p, primary_scrum_master_email: v, primary_scrum_master_name: u?.name || "" })); } }}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{users.filter(u => u.role !== "viewer").map(u => <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Backup Scrum Master</Label>
              <Select value={editConfig.backup_scrum_master_email || "none"} onValueChange={v => { if (v === "none") setEditConfig(p => ({ ...p, backup_scrum_master_email: "", backup_scrum_master_name: "" })); else { const u = users.find(u => u.email === v); setEditConfig(p => ({ ...p, backup_scrum_master_email: v, backup_scrum_master_name: u?.name || "" })); } }}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{users.filter(u => u.role !== "viewer").map(u => <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Stale Threshold (days)</Label><Input type="number" value={editConfig.stale_threshold_days || 3} onChange={e => setEditConfig(p => ({ ...p, stale_threshold_days: parseInt(e.target.value) || 3 }))} min={1} max={30} /><p className="text-[10px] text-slate-400 mt-0.5">Tasks with no activity beyond this are flagged stale</p></div>
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer"><input type="checkbox" checked={editConfig.auto_create_reactivation_tasks !== false} onChange={e => setEditConfig(p => ({ ...p, auto_create_reactivation_tasks: e.target.checked }))} className="rounded" />Auto-create reactivation tasks from dormant candidates</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowConfig(false)}>Cancel</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSaveConfig} data-testid="save-config-btn"><Save className="w-3.5 h-3.5 mr-1" />Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Commit Action Dialog */}
      <Dialog open={showCommit} onOpenChange={setShowCommit}>
        <DialogContent className="max-w-md" data-testid="commit-dialog">
          <DialogHeader><DialogTitle>Commit Action</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={commitForm.title} onChange={e => setCommitForm(p => ({ ...p, title: e.target.value }))} placeholder="What needs to happen?" data-testid="commit-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Section</Label>
                <Select value={commitForm.source_section} onValueChange={v => setCommitForm(p => ({ ...p, source_section: v }))}>
                  <SelectTrigger data-testid="commit-section"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operations">Operations</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="founder">Leader</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Due Time</Label>
                <Input type="time" value={commitForm.due_time} onChange={e => setCommitForm(p => ({ ...p, due_time: e.target.value }))} data-testid="commit-time" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Client (optional)</Label>
              <Input value={commitForm.client_name} onChange={e => setCommitForm(p => ({ ...p, client_name: e.target.value }))} placeholder="e.g., First Atlantic" data-testid="commit-client" />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={commitForm.founder_escalation} onChange={e => setCommitForm(p => ({ ...p, founder_escalation: e.target.checked }))} className="rounded" />
              Requires founder decision
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommit(false)}>Cancel</Button>
            <Button disabled={!commitForm.title} onClick={async () => {
              await fetch(`${API}/scrum/v2/commitments`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(commitForm) });
              toast.success("Committed"); setShowCommit(false); fetchAll();
            }} data-testid="submit-commit">Commit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion Dialog: finished, or now with them? */}
      <Dialog open={!!followUpTask} onOpenChange={(open) => { if (!open) setFollowUpTask(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Task done
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600"><strong>{followUpTask?.title}</strong> is marked done.</p>

          {completionMode === "choose" && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Button variant="outline" className="h-auto py-4 flex-col items-start" onClick={() => setCompletionMode("finished")} data-testid="completion-finished-btn">
                <span className="font-semibold text-slate-800">Finished</span>
                <span className="text-xs text-slate-500 font-normal text-left mt-1">Nothing more to do. Optionally add a follow-up.</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col items-start border-[#00A6DA]/40" onClick={() => setCompletionMode("waiting")} data-testid="completion-waiting-btn">
                <span className="font-semibold text-[#174987]">Now with them</span>
                <span className="text-xs text-slate-500 font-normal text-left mt-1">Waiting on someone else. Comes off the board.</span>
              </Button>
            </div>
          )}

          {completionMode === "waiting" && (
            <div className="mt-3">
              <p className="text-sm text-slate-600">The ball is with them. This leaves the board and becomes a wait.</p>
              <div className="mt-2">
                <Label className="text-xs">When should we chase?</Label>
                <Input type="date" value={waitChaseDate} onChange={(e) => setWaitChaseDate(e.target.value)} data-testid="wait-chase-date" />
              </div>
              <DialogFooter className="gap-2 mt-4">
                <Button variant="ghost" onClick={() => setCompletionMode("choose")}>Back</Button>
                <Button className="bg-[#174987] hover:bg-[#07152D] text-white" disabled={!waitChaseDate} data-testid="create-wait-btn"
                  onClick={async () => {
                    const res = await fetch(`${API}/waits/from-task/${followUpTask.task_id}`, {
                      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
                      body: JSON.stringify({ title: followUpTask.title, client_id: followUpTask.client_id || "", client_name: followUpTask.client_name || "", opportunity_id: followUpTask.opportunity_id || "", chase_date: waitChaseDate }),
                    });
                    if (res.ok) { toast.success("Now a wait — off the board. It will surface to chase on the date you set."); }
                    else { const e = await res.json().catch(() => null); toast.error(e?.detail || "Could not create wait"); }
                    setFollowUpTask(null); fetchAll();
                  }}>Create wait</Button>
              </DialogFooter>
            </div>
          )}

          {completionMode === "finished" && (
            <>
              <p className="text-sm text-slate-500 mt-2">Add a follow-up action?</p>
              <div className="space-y-2 mt-2">
                <Input
                  placeholder="e.g., Schedule review meeting, Send follow-up email..."
                  value={followUpTitle}
                  onChange={(e) => setFollowUpTitle(e.target.value)}
                  data-testid="follow-up-title-input"
                />
                <div>
                  <Label className="text-xs">Due Date <span className="text-red-500">*</span></Label>
                  <Input type="date" required value={followUpDueDate} onChange={(e) => setFollowUpDueDate(e.target.value)} data-testid="follow-up-due-date" />
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setFollowUpTask(null)} data-testid="no-follow-up-btn">No Follow-up</Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={!followUpTitle.trim() || !followUpDueDate}
                  data-testid="create-follow-up-btn"
                  onClick={async () => {
                    const payload = {
                      title: followUpTitle.trim(),
                      description: `Follow-up from completed task: ${followUpTask.title}`,
                      task_type: followUpTask.task_type || "general",
                      priority: followUpTask.priority || "medium",
                      assigned_to_id: followUpTask.assigned_to_id || "",
                      assigned_to_name: followUpTask.assigned_to_name || "",
                      client_name: followUpTask.client_name || "",
                      due_date: followUpDueDate,
                    };
                    const res = await fetch(`${API}/scrum/v2/tasks`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(payload) });
                    if (res.ok) { toast.success("Follow-up task created"); fetchAll(); }
                    else { toast.error("Failed to create follow-up"); }
                    setFollowUpTask(null);
                    setFollowUpTitle("");
                  }}
                >Create Follow-up</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
